import React from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core/styles'
import Divider from '@material-ui/core/Divider'
import { LongCustomButton, OkDialogButton } from '../custom-material/button/Button'
import { CenteredCustomTypography } from '../custom-material/typography/Typography'
import { uploadData, putData, postData, getData, getBlob } from '../../libs/api'
import { saveAs } from 'file-saver'
import { contactsValidState, validationMessages } from '../../libs/validators'
import { isEmpty, startCase } from 'lodash'
import CustomDialog from '../custom-material/dialog/Dialog'
import CustomDialogTitle from '../custom-material/dialog/DialogTitle'
import ErrorIcon from '@material-ui/icons/Error'

const styles = theme => ({
  excelAddContainer: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    padding: 6,
    width: 148,
    backgroundColor: '#EDEDED'
  },
  excelAddText: {
    color: '#283572',
    fontWeight: 'bold',
    lineHeight: 1.15,
    marginBottom: 10
  },
  excelFileName: {
    color: '#283572',
    fontStyle: 'italic',
    marginBottom: 10
  },
  excelAddDivider: {
    height: 2,
    marginBottom: 10,
    width: '90%',
    backgroundColor: '#283572'
  }
})

const BulkAddUsersInvalidDialog = ({classes, users, validStates, dialogOpen, handleCloseDialog}) => {
  const invalidRowCount = validStates.filter(x => !x.isValid).length
  const toMessage = message => validationMessages[message] || message
  return (
    <CustomDialog open={dialogOpen} onClose={handleCloseDialog} aria-labelledby='updates-dialog-title'>
      <CustomDialogTitle id='updates-dialog-title' disableTypography>
        <ErrorIcon />
        Invalid submission
      </CustomDialogTitle>
      <div className={classes.contentContainer}>
        <CenteredCustomTypography variant='body1' paragraph>
          There were {invalidRowCount} users found with errors
          {invalidRowCount > 5 ? <React.Fragment><br />The first five are shown below:</React.Fragment> : ':'}
        </CenteredCustomTypography>
        {users.map((user, index) => ({user, index, validState: validStates[index]})).filter(x => !x.validState.isValid).map(x =>
          <CenteredCustomTypography key={x.index} variant='body1'>
            Row {x.index + 1} ({x.user.firstName} {x.user.lastName} {x.user.emailAddress}):<br />
            {Object.entries(x.validState).filter(x => !isEmpty(x[1])).map(x => `${startCase(x[0])} ${toMessage(x[1])}`).join('; ')}
          </CenteredCustomTypography>
        ).slice(0, 5)}
        <br />
        <div className={classes.actionContainer}>
          <OkDialogButton variant='contained' size='small' color='secondary' onClick={handleCloseDialog}>OK</OkDialogButton>
        </div>
      </div>
    </CustomDialog>
  )
}

class BulkAddUsersComponent extends React.PureComponent {
  constructor (props) {
    super(props)

    this.state = { submitEnabled: false, fileName: null, users: [], validStates: [], dialogOpen: false }

    this.handleFileChange = async (event) => {
      const file = event.target.files[0]
      const emailAddress = this.props.emailAddress
      const rUpload = await postData(`contacts/${emailAddress}/bulk`, { name: file.name, type: file.type })
      await uploadData(rUpload.uploadURL, file)

      const rParse = await putData(`contacts/${emailAddress}/bulk/${rUpload.key}`)
      const validStates = contactsValidState(rParse.users)
      const isValid = validStates.every(x => x.isValid)
      this.setState({ submitEnabled: isValid, fileName: file.name, users: rParse.users, validStates, dialogOpen: !isValid })
    }

    this.handleCloseDialog = () => this.setState({ submitEnabled: false, fileName: null, users: [], validStates: [], dialogOpen: false })

    this.handleDownload = async (event) => {
      event.preventDefault()

      const rDownload = await getData(`contacts/${this.props.emailAddress}/bulk`)
      const blob = await getBlob(rDownload.url)
      saveAs(blob, rDownload.name)
    }

    this.handleSubmit = () => {
      this.props.addBulkUsers(this.state.users)
      this.setState({ submitEnabled: false, fileName: null, users: [], validStates: [], dialogOpen: false })
    }
  }

  render () {
    const { submitEnabled, fileName, users, dialogOpen, validStates } = this.state
    const { classes } = this.props

    return (
      <div className={classes.excelAddContainer}>
        <CenteredCustomTypography style={{width: '100%'}} className={classes.excelAddText} variant='body1'>Prefer to use an Excel template?</CenteredCustomTypography>
        <CenteredCustomTypography className={classes.excelAddText} variant='body1'>Step 1:</CenteredCustomTypography>
        <LongCustomButton style={{width: 135, padding: 3, marginBottom: 10}} variant='contained' color='secondary' size='small' onClick={this.handleDownload}>
          Download Template
        </LongCustomButton>
        <Divider className={classes.excelAddDivider} />

        <CenteredCustomTypography className={classes.excelAddText} variant='body1'>Step 2:</CenteredCustomTypography>
        <input accept='.xlsx' className={classes.fileInput} style={{ display: 'none' }} id='upload-template-file' type='file' value='' onChange={this.handleFileChange} />
        <label htmlFor='upload-template-file'>
          <LongCustomButton style={{width: 135, padding: 3, marginBottom: 10}} variant='contained' color='secondary' size='small' component='span'>
            Upload Template
          </LongCustomButton>
        </label>
        <CenteredCustomTypography className={classes.excelFileName} variant='body1'>
          {fileName || <span>&lt;File Name&gt;</span>}
        </CenteredCustomTypography>
        <Divider className={classes.excelAddDivider} />

        <CenteredCustomTypography className={classes.excelAddText} variant='body1'>Step 3:</CenteredCustomTypography>
        <LongCustomButton style={{width: 135, padding: 3, marginBottom: 10}} variant='contained' color='secondary' size='small'
          disabled={!submitEnabled} onClick={this.handleSubmit}>
          Submit {submitEnabled && users.length ? `${users.length} users` : ''}
        </LongCustomButton>

        {dialogOpen && <BulkAddUsersInvalidDialog classes={classes} users={users} validStates={validStates} dialogOpen={dialogOpen} handleCloseDialog={this.handleCloseDialog} />}
      </div>
    )
  }
}

BulkAddUsersComponent.propTypes = {
  classes: PropTypes.object.isRequired,
  emailAddress: PropTypes.string.isRequired,
  addBulkUsers: PropTypes.func.isRequired
}

export default withStyles(styles)(BulkAddUsersComponent)
